import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import type { Student } from '@skjv/common';
import { DownloadIcon, UploadIcon, CheckCircleIcon, XCircleIcon } from '@components/icons';
import { ImportGroupDialog } from './import-group-dialog';

interface PendingGroupImport {
  pendingData: Record<string, unknown>;
  students: Student[];
}

/**
 * Export content - export studies to JSON or PDF.
 */
export const ExportContent = observer(function ExportContent() {
  const [importStatus, setImportStatus] = useState<{ success: boolean; message: string } | null>(
    null,
  );
  const [pendingImport, setPendingImport] = useState<PendingGroupImport | null>(null);
  const selectedStudy = bibleState.selectedStudy;

  const handleExportJson = () => {
    const skjvData = bibleState.exportAllStudiesToJson();
    const blob = new Blob([skjvData], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `skjv-studies-${new Date().toISOString().split('T')[0]}.skjv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportSelectedJson = () => {
    if (!selectedStudy) return;
    const skjvData = bibleState.exportStudyToJson(selectedStudy.id);
    if (!skjvData) return;

    const blob = new Blob([skjvData], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedStudy.name.replace(/[^a-z0-9]/gi, '-')}.skjv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const importJsonContent = (json: string) => {
    try {
      const result = bibleState.importStudiesFromJson(json);

      if (result.needsGroup) {
        setPendingImport({ pendingData: result.pendingData, students: result.students });
        return;
      }

      const duplicates = (result.duplicateStudies ?? 0) + (result.duplicateGroups ?? 0);
      const summary = `Imported ${result.studiesCount} ${result.studiesCount === 1 ? 'study' : 'studies'} and ${result.groupsCount} ${result.groupsCount === 1 ? 'group' : 'groups'}.`;
      const dupNote =
        duplicates > 0
          ? ` ${duplicates} ${duplicates === 1 ? 'item was' : 'items were'} already in your library and ${duplicates === 1 ? 'was' : 'were'} skipped.`
          : '';
      setImportStatus({ success: true, message: summary + dupNote });
      // The import-done dialog — including what was deduplicated, so a
      // re-import never looks like silent data loss (UAT 2026-08-26).
      bibleState.showConfirmDialog({
        title: 'Import complete',
        message: summary + dupNote,
        confirmText: 'OK',
        onConfirm: () => {},
      });
    } catch {
      setImportStatus({ success: false, message: 'Failed to import file' });
    }
  };

  const handleGroupConfirm = (groupName: string) => {
    if (!pendingImport) return;

    const result = bibleState.importRawStudyWithGroup(pendingImport.pendingData, groupName);
    setPendingImport(null);
    setImportStatus({
      success: true,
      message: `Imported ${result.studiesCount} study and ${result.groupsCount} group`,
    });
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.name.endsWith('.skjvz') || file.name.endsWith('.zip')) {
      try {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(file);
        const skjvFile = Object.keys(zip.files).find(
          (name) => name.endsWith('.skjv') || name.endsWith('.json'),
        );
        if (!skjvFile) {
          setImportStatus({ success: false, message: 'No study file found in archive' });
          return;
        }
        const content = await zip.files[skjvFile].async('string');
        importJsonContent(content);
      } catch {
        setImportStatus({ success: false, message: 'Failed to read archive' });
      }
    } else {
      const reader = new FileReader();
      reader.onload = (e) => importJsonContent(e.target?.result as string);
      reader.readAsText(file);
    }

    event.target.value = '';
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Export all studies */}
      <div className="rounded-xl border border-stone-300 bg-stone-50/50 p-4 dark:border-stone-600 dark:bg-stone-800/50">
        <h3 className="mb-2 font-medium text-stone-800 dark:text-stone-200">Export All Studies</h3>
        <p className="mb-3 text-sm text-stone-500 dark:text-stone-400">
          Download all studies and study groups as a JSON file.
        </p>
        <button
          onClick={handleExportJson}
          className="flex items-center gap-2 rounded-lg bg-cyan-500 px-4 py-2 text-white transition-colors hover:bg-cyan-600"
        >
          <DownloadIcon className="h-4 w-4" />
          Export JSON
        </button>
      </div>

      {/* Export selected study */}
      {selectedStudy && (
        <div className="rounded-xl border border-stone-300 bg-stone-50/50 p-4 dark:border-stone-600 dark:bg-stone-800/50">
          <h3 className="mb-2 font-medium text-stone-800 dark:text-stone-200">
            Export "{selectedStudy.name}"
          </h3>
          <div className="flex gap-2">
            <button
              onClick={handleExportSelectedJson}
              className="flex items-center gap-2 rounded-lg bg-stone-200 px-4 py-2 text-stone-700 transition-colors hover:bg-stone-300 dark:bg-stone-700 dark:text-stone-200 dark:hover:bg-stone-600"
            >
              <DownloadIcon className="h-4 w-4" />
              JSON
            </button>
            <button
              onClick={() => bibleState.showToast('PDF export coming soon!', 'info')}
              className="flex items-center gap-2 rounded-lg bg-stone-200 px-4 py-2 text-stone-700 transition-colors hover:bg-stone-300 dark:bg-stone-700 dark:text-stone-200 dark:hover:bg-stone-600"
            >
              <DownloadIcon className="h-4 w-4" />
              PDF
            </button>
          </div>
        </div>
      )}

      {/* Import */}
      <div className="rounded-xl border border-stone-300 bg-stone-50/50 p-4 dark:border-stone-600 dark:bg-stone-800/50">
        <h3 className="mb-2 font-medium text-stone-800 dark:text-stone-200">Import Studies</h3>
        <p className="mb-3 text-sm text-stone-500 dark:text-stone-400">
          Import studies from a previously exported .skjv, .skjvz, .json, or .zip file.
        </p>
        <label className="flex w-fit cursor-pointer items-center gap-2 rounded-lg bg-stone-200 px-4 py-2 text-stone-700 transition-colors hover:bg-stone-300 dark:bg-stone-700 dark:text-stone-200 dark:hover:bg-stone-600">
          <UploadIcon className="h-4 w-4" />
          Choose File
          <input type="file" accept=".skjv,.skjvz,.json,.zip" onChange={handleImport} className="hidden" />
        </label>

        {importStatus && (
          <div
            className={`mt-3 flex items-center gap-2 text-sm ${
              importStatus.success
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}
          >
            {importStatus.success ? (
              <CheckCircleIcon className="h-4 w-4" />
            ) : (
              <XCircleIcon className="h-4 w-4" />
            )}
            {importStatus.message}
          </div>
        )}
      </div>

      {pendingImport && (
        <ImportGroupDialog
          students={pendingImport.students}
          onConfirm={handleGroupConfirm}
          onCancel={() => setPendingImport(null)}
        />
      )}
    </div>
  );
});
